/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useEffect, useState } from "react";
import { FormikProps } from "formik";
import classNames from "clsx";
import dayjs from "dayjs";

import { FaPlus, FaTrash } from "react-icons/fa";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";

import { useApi } from "@/hooks/useApi";
import { usePartogramFetus } from "@/hooks/usePartogramFetus";
import { FetusModalStatus } from "@/enums/FetusModalStatus";
import { AmnioticSac } from "@/enums/AmnioticSac";
import { AmnioticFluid } from "@/enums/AmnioticFluid";
import { DeLeePlan, DeLeePlanMessages } from "@/enums/DeLeePlan";
import {
	ContractionsPerSecond,
	ContractionsPerSecondMessages,
} from "@/enums/ContractionsPerSecond";
import { addFetus } from "@/utils/initialValues/addFetus";
import { createSentence } from "@/utils/createSentence";
import { partogramFillingLogsSentences } from "@/utils/mappings/partogramFillingLogsSentences";

import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { ProfessionalWithRolesSelect } from "@/components/Selects/ProfessionalWithRolesSelect";
import { Timeline } from "@/components/Timeline";
import { FormattedFormControl } from "@/components/FormControl/FormattedFormControl";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { FormRadio } from "@/components/Radio/FormRadio";
import { Divider } from "@/components/Divider";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { MedicationsSelect } from "@/components/Selects/MedicationsSelect";
import { VitalSigns } from "@/components/VitalSigns";

import "./styles.scss";

type AddFetusInitialValues = typeof addFetus;

type PaginatedPartogramFillingLogs = GenericPagination<PartogramFillingLog>;

type AddFetusFormProps = {
	formik: FormikProps<AddFetusInitialValues>;
	skeleton: boolean;
	isLoading: boolean;
	onCancel: () => void;
	isAmnioticSacRuptured?: boolean;
};

export function AddFetusForm({
	formik,
	skeleton,
	isLoading,
	onCancel,
	isAmnioticSacRuptured = false,
}: AddFetusFormProps) {
	const { api } = useApi();
	const { fetusModalStatus, currentPartogramDataId } = usePartogramFetus();

	const isViewing = fetusModalStatus === FetusModalStatus.Viewing;
	const isEditing = fetusModalStatus === FetusModalStatus.Editing;

	const MAXIMUM_NUMBER_OF_CONTRACTIONS_PER_SECOND_VALUES = 6;
	const contractionsPerSecondLabels = [
		"0-10 min",
		"10-20 min",
		"20-30 min",
		"30-40 min",
		"40-50 min",
		"50-60 min",
	];

	function handleAddContractionsPerSecondField() {
		formik.setFieldValue("contractions_per_second", [
			...formik.values.contractions_per_second,
			"",
		]);
	}

	function handleRemoveContractionsPerSecondField(indexToRemove: number) {
		formik.setFieldValue(
			"contractions_per_second",
			formik.values.contractions_per_second.filter((_, index) => index !== indexToRemove)
		);
	}

	function handleChangeContractionPerSecondField(index: number, value: string) {
		formik.setFieldValue(
			"contractions_per_second",
			formik.values.contractions_per_second.map((currentValue, currentIndex) => {
				if (currentIndex === index) {
					return value;
				}

				return currentValue;
			})
		);
	}

	function ButtonPlus() {
		return (
			<Button
				variant="secondary"
				size="sm"
				className="h-50px w-fit-content"
				onClick={handleAddContractionsPerSecondField}
				disabled={isViewing}
			>
				<FaPlus />
			</Button>
		);
	}

	useEffect(() => {
		if (!formik.values.examiner_id) {
			formik.setFieldValue("examiner_label", "");
		}
	}, [formik.values.examiner_id]);

	useEffect(() => {
		if (Number(formik.values.medication_taken) === 0) {
			formik.setFieldValue("product_id", "");
		}
	}, [formik.values.medication_taken]);

	useEffect(() => {
		if (!formik.values.product_id) {
			formik.setFieldValue("product_label", "");
		}
	}, [formik.values.product_id]);

	useEffect(() => {
		formik.setFieldValue("amniotic_sac", isAmnioticSacRuptured ? AmnioticSac.Ruptured : "");
	}, [isAmnioticSacRuptured]);

	const [isLoadingPartogramFillingLogs, setIsLoadingPartogramFillingLogs] = useState(false);
	const [isLoadingNextPartogramFillingLogs, setIsLoadingNextPartogramFillingLogs] =
		useState(false);
	const [partogramFillingLogs, setPartogramFillingLogs] = useState<PaginatedPartogramFillingLogs>(
		{} as PaginatedPartogramFillingLogs
	);

	async function fetchPartogramFillingLogs(next?: boolean) {
		try {
			if (currentPartogramDataId) {
				if (next) {
					setIsLoadingNextPartogramFillingLogs(true);
				} else {
					setIsLoadingPartogramFillingLogs(true);
				}

				const baseApiUrl = `/partogram/${currentPartogramDataId}/logs`;
				const url = next ? partogramFillingLogs.links?.next || baseApiUrl : baseApiUrl;

				const { data } = await api.get(url);

				const oldPartogramFillingLogsResults = partogramFillingLogs.results ?? [];
				const results = next
					? [...oldPartogramFillingLogsResults, ...data.results]
					: data.results;

				setPartogramFillingLogs({ ...data, results });
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoadingPartogramFillingLogs(false);
			setIsLoadingNextPartogramFillingLogs(false);
		}
	}

	useEffect(() => {
		if (currentPartogramDataId && isViewing) {
			fetchPartogramFillingLogs();
		}
	}, [currentPartogramDataId, fetusModalStatus]);

	return (
		<Form
			onSubmit={formik.handleSubmit}
			className={classNames(isViewing && "viewing-add-fetus-form")}
		>
			{isViewing &&
				(!isLoadingPartogramFillingLogs && !partogramFillingLogs.results?.length ? null : (
					<div className="px-10 pb-10 w-100">
						<TagHTMLOrSkeleton
							text="Últimas Modificações"
							tag="p"
							className="font-weight-bold h6 mb-6"
							skeleton={isLoadingPartogramFillingLogs}
							skeletonClassName="mb-6"
							skeletonWidth="20%"
						/>

						{isLoadingPartogramFillingLogs ? (
							<SkeletonWrapper width="40%" count={3} />
						) : (
							<Timeline className="line-spacing">
								{partogramFillingLogs.results?.map((fillingLog) => {
									const words = fillingLog.attributes.map(
										(attribute) =>
											partogramFillingLogsSentences[
												attribute as keyof typeof partogramFillingLogsSentences
											]
									);

									const hasMoreThanOneAttribute =
										words.filter(Boolean).length > 1;
									const prefix = hasMoreThanOneAttribute
										? "Alterações nos campos "
										: "Alteração no campo ";

									return (
										<Timeline.Item>
											<Timeline.Label className="line-spacing">
												<div className="d-flex flex-column">
													<span>
														{dayjs(fillingLog.datetime).format("HH:mm")}
													</span>
													<span className="fs-7 text-gray-700">
														{dayjs(fillingLog.datetime).format(
															"DD/MM/YYYY"
														)}
													</span>
												</div>
											</Timeline.Label>
											<Timeline.Content badgeContainerClassName="ms-7">
												{createSentence(prefix, words)}
											</Timeline.Content>
										</Timeline.Item>
									);
								})}
							</Timeline>
						)}

						{(isLoadingPartogramFillingLogs || partogramFillingLogs.links?.next) &&
							(isLoadingNextPartogramFillingLogs ? (
								<Spinner variant="primary" animation="border" className="mt-4" />
							) : (
								<TagHTMLOrSkeleton
									text="Ver mais"
									tag="div"
									role="button"
									className="text-primary mt-4 w-fit-content"
									onClick={() => fetchPartogramFillingLogs(true)}
									skeleton={isLoadingPartogramFillingLogs}
									skeletonClassName="mt-4"
									skeletonWidth="10%"
								/>
							))}
					</div>
				))}

			<Row className="justify-content-between gap-4 px-10">
				<Col lg={5} md={6} xs={12}>
					{skeleton ? (
						<SkeletonWrapper
							containerClassName="d-flex flex-grow-1 gap-8"
							width="50%"
							height={40}
							count={2}
							inline
						/>
					) : (
						<div className="d-flex align-items-center gap-4">
							<label className="fw-bold h6" htmlFor="examiner">
								Examinador
							</label>

							<ProfessionalWithRolesSelect
								isSolid
								className="w-100"
								inputId="examiner"
								menuPlacement="auto"
								roles={["Técnico de Enfermagem", "Enfermeiro"]}
								professionalId={formik.values.examiner_id}
								value={{
									value: formik.values.examiner_id,
									label: formik.values.examiner_label,
								}}
								onChange={(option) => {
									const { label, value } = option as ReactSelectOption;

									formik.setFieldValue("examiner_label", label);
									formik.setFieldValue("examiner_id", value);
								}}
								isDisabled={isViewing}
							/>
						</div>
					)}
				</Col>

				<Col lg={5} md={6} xs={12} className="d-flex align-items-center gap-3">
					{skeleton ? (
						<SkeletonWrapper
							containerClassName="d-flex flex-grow-1 gap-8"
							width="20%"
							height={40}
							count={2}
							inline
						/>
					) : (
						<>
							<Form.Label className="mb-0 fw-bold h6" htmlFor="datetime">
								Hora
							</Form.Label>

							<FormattedFormControl
								id="datetime"
								type="datetime-local"
								{...formik.getFieldProps("datetime")}
								name="datetime"
								skeleton={skeleton}
								readOnly={isViewing}
							/>
						</>
					)}
				</Col>
			</Row>

			<Row className="mt-10 mb-7 px-10">
				<Col xs={12}>
					<FormGroupRadio
						name="dilation"
						label="Dilatação/cm"
						requestedValue={formik.values.dilation}
						onChange={formik.handleChange}
						labelClassName="fw-bold h6"
						skeleton={skeleton}
						disabled={isViewing}
						inline
					>
						<FormRadio label="01" value="1" id="dilation-01" />
						<FormRadio label="02" value="2" id="dilation-02" />
						<FormRadio label="03" value="3" id="dilation-03" />
						<FormRadio label="04" value="4" id="dilation-04" />
						<FormRadio label="05" value="5" id="dilation-05" />
						<FormRadio label="06" value="6" id="dilation-06" />
						<FormRadio label="07" value="7" id="dilation-07" />
						<FormRadio label="08" value="8" id="dilation-08" />
						<FormRadio label="09" value="9" id="dilation-09" />
						<FormRadio label="10" value="10" id="dilation-10" />
					</FormGroupRadio>
				</Col>
			</Row>

			<Divider />

			<Row className="mt-3 mb-7 px-10">
				<Col xs={12}>
					<FormGroupRadio
						name="delee_plan"
						label="Plano DeLee"
						requestedValue={formik.values.delee_plan}
						onChange={formik.handleChange}
						labelClassName="fw-bold h6"
						skeleton={skeleton}
						disabled={isViewing}
						inline
					>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.MinusThree]}
							value={DeLeePlan.MinusThree}
							id="delee-plan-minus-three"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.MinusTwo]}
							value={DeLeePlan.MinusTwo}
							id="delee-plan-minus-two"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.MinusOne]}
							value={DeLeePlan.MinusOne}
							id="delee-plan-minus-one"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.Zero]}
							value={DeLeePlan.Zero}
							id="delee-plan-zero"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.One]}
							value={DeLeePlan.One}
							id="delee-plan-one"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.Two]}
							value={DeLeePlan.Two}
							id="delee-plan-two"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.Three]}
							value={DeLeePlan.Three}
							id="delee-plan-three"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.Vulva]}
							value={DeLeePlan.Vulva}
							id="delee-plan-vulva"
						/>
						<FormRadio
							label={DeLeePlanMessages[DeLeePlan.Detachment]}
							value={DeLeePlan.Detachment}
							id="delee-plan-detachment"
						/>
					</FormGroupRadio>
				</Col>
			</Row>

			<Divider />

			<Row className="mt-3 mb-7 px-10">
				<Col xs={12} className="d-flex align-items-center gap-3">
					{skeleton ? (
						<SkeletonWrapper
							containerClassName="d-flex flex-grow-1 gap-8"
							width="10%"
							height={40}
							count={2}
							inline
						/>
					) : (
						<>
							<Form.Label className="mb-0 fw-bold h6" htmlFor="heart-rate">
								BFC - Batimentos/Min
							</Form.Label>

							<Col md={1}>
								<FormattedFormControl
									id="heart-rate"
									type="text"
									mask="000"
									isMaskedInput
									{...formik.getFieldProps("heart_rate")}
									name="heart_rate"
									skeleton={skeleton}
									readOnly={isViewing}
								/>
							</Col>
						</>
					)}
				</Col>
			</Row>

			<Divider />

			<Row className="mt-3 mb-7 px-10">
				<Col xs={12}>
					<TagHTMLOrSkeleton
						text="Contrações/seg"
						tag="p"
						className="font-weight-bold h6"
						skeleton={skeleton}
						skeletonWidth="15%"
					/>

					<div className="d-flex flex-column gap-5 pt-2 w-100">
						{formik.values.contractions_per_second.length ? (
							formik.values.contractions_per_second.map((value, index) => {
								const currentIndex = index + 1;

								return (
									<div
										key={`contractions-per-second-field-${index}`}
										className="d-flex gap-4"
									>
										<Row className="align-items-center gap-5 w-100">
											<Col
												xs={12}
												className="d-flex align-items-center gap-4"
											>
												{skeleton ? (
													<SkeletonWrapper
														containerClassName="d-flex flex-grow-1"
														width="20%"
														height={30}
													/>
												) : (
													<Form.Label
														className="mb-0"
														style={{ width: "5rem" }}
													>
														{contractionsPerSecondLabels[index] ?? "-"}
													</Form.Label>
												)}
												<FormGroupRadio
													name={`contractions_per_second_${index}`}
													skeleton={skeleton}
													disabled={isViewing}
													requestedValue={value ?? ""}
													onChange={(
														event: ChangeEvent<HTMLInputElement>
													) =>
														handleChangeContractionPerSecondField(
															index,
															event.target.value
														)
													}
													inline
												>
													<FormRadio
														label={
															ContractionsPerSecondMessages[
																ContractionsPerSecond.ZeroToNineTeen
															]
														}
														value={ContractionsPerSecond.ZeroToNineTeen}
														id={`contractions-per-second-zero-to-nine-teen-${index}`}
													/>
													<FormRadio
														label="20-39 seg"
														value={
															ContractionsPerSecond.TwentyToThirtyNine
														}
														id={`contractions-per-second-twenty-to-thirty-nine-${index}`}
													/>
													<FormRadio
														label="40 seg"
														value={ContractionsPerSecond.Forty}
														id={`contractios-per-second-forty-${index}`}
													/>
												</FormGroupRadio>

												{skeleton ? (
													<SkeletonWrapper
														containerClassName="d-flex flex-grow-1 gap-4"
														width="15%"
														height={40}
														count={2}
														inline
													/>
												) : (
													<>
														<Button
															variant="secondary"
															size="sm"
															className="h-50px"
															disabled={isViewing}
															onClick={() =>
																handleRemoveContractionsPerSecondField(
																	index
																)
															}
														>
															<FaTrash />
														</Button>

														{currentIndex ===
															formik.values.contractions_per_second
																.length &&
															currentIndex !==
																MAXIMUM_NUMBER_OF_CONTRACTIONS_PER_SECOND_VALUES && (
																<ButtonPlus />
															)}
													</>
												)}
											</Col>
										</Row>
									</div>
								);
							})
						) : (
							<ButtonPlus />
						)}
					</div>
				</Col>
			</Row>

			<Divider />

			<Row className="mt-3 mb-7 px-10 gap-4 justify-content-between">
				<Col xs={12} md={3}>
					<FormGroupRadio
						name="amniotic_sac"
						label="Bolsa"
						requestedValue={formik.values.amniotic_sac}
						onChange={formik.handleChange}
						labelClassName="fw-bold h6"
						skeleton={skeleton}
						disabled={isAmnioticSacRuptured || isViewing}
						inline
					>
						<FormRadio
							label="Íntegra"
							value={AmnioticSac.Intact}
							id="amniotic-sac-intact"
						/>
						<FormRadio
							label="Rompida"
							value={AmnioticSac.Ruptured}
							id="amniotic-sac-ruptured"
						/>
					</FormGroupRadio>
				</Col>

				<Col xs={12} md={7}>
					<FormGroupRadio
						name="amniotic_fluid"
						label="Líquido Amniótico"
						requestedValue={formik.values.amniotic_fluid}
						onChange={formik.handleChange}
						labelClassName="fw-bold h6"
						skeleton={skeleton}
						disabled={isViewing}
						inline
					>
						<FormRadio
							label="Claro"
							value={AmnioticFluid.Clear}
							id="amniotic-fluid-clear"
						/>
						<FormRadio
							label="Com grumos"
							value={AmnioticFluid.WithLumps}
							id="amniotic-fluid-with-lumps"
						/>
						<FormRadio
							label="Sem grumos"
							value={AmnioticFluid.WithoutLumps}
							id="amniotic-fluid-without-lumps"
						/>
						<FormRadio
							label="Meconial"
							value={AmnioticFluid.Meconium}
							id="amniotic-fluid-meconium"
						/>
					</FormGroupRadio>
				</Col>
			</Row>

			<Divider />

			<Row className="mt-3 mb-7 px-10 align-items-center">
				<Col xs={12} md={3}>
					<FormGroupRadio
						name="medication_taken"
						label="Medicação"
						requestedValue={formik.values.medication_taken}
						onChange={formik.handleChange}
						labelClassName="fw-bold h6"
						skeleton={skeleton}
						disabled={isViewing}
						inline
					>
						<FormRadio label="Sim" value="1" id="medication-taken-yes" />
						<FormRadio label="Não" value="0" id="medication-taken-no" />
					</FormGroupRadio>
				</Col>

				{Number(formik.values.medication_taken) === 1 &&
					(skeleton ? (
						<SkeletonWrapper
							containerClassName="d-flex flex-grow-1 gap-8 w-50"
							width="30%"
							height={40}
						/>
					) : (
						<Col xs={12} md={4}>
							<MedicationsSelect
								isDisabled={isViewing}
								value={{
									value: formik.values.product_id,
									label: formik.values.product_label,
								}}
								onChange={(option) => {
									const { label, value } = option as ReactSelectOption;

									formik.setFieldValue("product_label", label);
									formik.setFieldValue("product_id", value);
								}}
							/>
						</Col>
					))}
			</Row>

			<Divider />

			<Row className="mt-3 mb-4 px-10 justify-content-between gap-4">
				<Col xs={12} md={6} lg={5}>
					<VitalSigns.BloodPressure
						systolicInputProps={{
							name: "systolic_blood_pressure",
							onChange: formik.handleChange,
							value: formik.values.systolic_blood_pressure,
							readOnly: isViewing,
						}}
						diastolicInputProps={{
							name: "diastolic_blood_pressure",
							onChange: formik.handleChange,
							value: formik.values.diastolic_blood_pressure,
							readOnly: isViewing,
						}}
						skeleton={skeleton}
						labelClassName="fw-bold h6 text-nowrap"
						inputClassName="mb-2"
						containerClassName="d-flex align-items-center gap-4"
						solid={false}
					/>
				</Col>

				<Col xs={12} md={4} className="d-flex align-items-center gap-3 mb-5">
					{skeleton ? (
						<SkeletonWrapper
							containerClassName="d-flex flex-grow-1 gap-8"
							width="30%"
							height={40}
							count={2}
							inline
						/>
					) : (
						<>
							<Form.Label className="mb-0 fw-bold h6" htmlFor="pulse">
								Pulso
							</Form.Label>

							<Col md={4}>
								<FormattedFormControl
									id="pulse"
									type="text"
									mask="000"
									isMaskedInput
									{...formik.getFieldProps("pulse")}
									name="pulse"
									skeleton={skeleton}
									readOnly={isViewing}
								/>
							</Col>
						</>
					)}
				</Col>
			</Row>

			<Row className="mb-4 px-10">
				<Col xs={12} md={4}>
					<VitalSigns.Temperature
						id="temperature"
						{...formik.getFieldProps("temperature")}
						name="temperature"
						skeleton={skeleton}
						labelClassName="fw-bold h6 text-nowrap"
						inputClassName="mb-2"
						containerClassName="d-flex align-items-center gap-4"
						solid={false}
						readOnly={isViewing}
					/>
				</Col>
			</Row>

			<Row className="mb-7 px-10">
				<Col xs={12}>
					<FormattedFormControl
						as="textarea"
						id="add-fetus-observations"
						label="Observações:"
						labelClassName="fw-bold h6"
						placeholder="Descreva"
						{...formik.getFieldProps("observations")}
						name="observations"
						skeleton={skeleton}
						skeletonHeight={60}
						maxLength={2000}
						readOnly={isViewing}
						displayCharactersRemaining
					/>
				</Col>
			</Row>

			<div className="px-10 d-flex justify-content-end gap-4">
				<Button
					variant="secondary"
					type="button"
					onClick={onCancel}
					className={classNames(isViewing && "px-10")}
				>
					{isViewing ? "Sair" : "Cancelar"}
				</Button>
				{!isViewing && (
					<Button variant="primary" type="submit" disabled={isLoading}>
						{isLoading ? (
							<Spinner
								as="span"
								animation="border"
								size="sm"
								role="status"
								aria-hidden="true"
							/>
						) : isEditing ? (
							"Editar"
						) : (
							"Salvar"
						)}
					</Button>
				)}
			</div>
		</Form>
	);
}
